import { useTranslation } from 'react-i18next';
import { LADDER_LABEL_KEY, rungMark } from '@d-orca/shared/client/activities';
import { Pill } from '@components/ui/Pill/Pill';
import { Tooltip, TooltipContent } from '@components/ui/Tooltip/Tooltip';
import { FlagIcon } from '@utils/icons';
import { rungClass, type LadderRung } from '../../lib/ladder';
import statusStyles from '../_status.module.css';
import styles from './LadderChip.module.css';

// The ladder rung as a chip — the rail's status cell and the drawer header
// share it so the two can't disagree about what a rung looks like. `dot`
// marks a committed rung (position 1+): a glance tells promised from planned
// even when the chip is squeezed to a dot itself. Delayed and overdue share the
// late red; overdue's mark is a FLAG — on the chip and on the dot — so the two
// stay apart without a second hue.

interface ILadderChipProps {
  rung: LadderRung;
  /** Squeezed rail: the chip degrades to a coloured dot; the label moves to
   *  its aria-label and a marker tooltip (the dot is a data mark). */
  asDot?: boolean;
  /** A bare dot for a legend or an option row whose label already sits beside
   *  it: no tooltip, and hidden from assistive tech (the label says it). */
  asSwatch?: boolean;
  /** How many days late a delayed or overdue activity is — the chip says
   *  "+N d" after its label (the Daily List's card). Nothing when 0. */
  lateDays?: number;
  className?: string;
}

export function LadderChip({ rung, asDot = false, asSwatch = false, lateDays = 0, className = '' }: ILadderChipProps) {
  const { t } = useTranslation();
  const label = t(LADDER_LABEL_KEY[rung]);
  const tone = statusStyles[rungClass(rung)] ?? '';
  const mark = rungMark(rung);
  const flagged = mark === 'flag';
  const dotClass = `${styles.dot} ${flagged ? styles.flagDot : ''} ${tone} ${className}`;
  const flag = flagged ? <FlagIcon size={10} /> : null;
  if (asSwatch) return <span className={dotClass} aria-hidden data-rung={rung}>{flag}</span>;
  if (asDot) {
    return (
      <Tooltip variant="marker" content={<TooltipContent title={label} />}>
        <span className={`${dotClass} ${styles.markDot}`} role="img" aria-label={label} data-rung={rung}>{flag}</span>
      </Tooltip>
    );
  }
  return (
    <Pill className={`${styles.chip} ${tone} ${className}`} data-rung={rung}>
      {flagged ? (
        <span className={styles.flag} aria-hidden>
          <FlagIcon size={12} />
        </span>
      ) : (
        mark === 'point' && <span className={styles.mark} aria-hidden />
      )}
      <span className={styles.label}>{label}</span>
      {lateDays > 0 && <b className={styles.late}>{t('scheduler.ladder.lateDays', { days: lateDays })}</b>}
    </Pill>
  );
}
